import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { BiaUtils } from 'src/@utility/bia.utils';
import { RequestService } from 'src/app/core/service/request.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ImpactService {
  constructor(private restApi: RequestService, private http: HttpClient) {}

  createBiaWithWorkflow(data) {
    return this.restApi.request(
      'POST',
      '/api/v5/bias/create-with-workflow',
      data
    );
  }

  updateBiaWorkflow(data) {
    return this.restApi.request('PUT', `/api/v5/bia/update-workflow`, data);
  }

  getBccBia(options = {}) {
    return this.restApi.request('GET', `/api/v5/bias/bcc`, {
      ...options,
      'isEnabled.equals': true,
      sort: 'id,desc',
    });
  }

  getBiaByOrgId(options = {}) {
    return this.restApi.request('GET', '/api/bias', {
      ...options,
      'isEnabled.equals': true,
      page: 0,
      size: 2147483647,
    });
  }

  getBiaDetail(biaId) {
    return this.restApi.get(
      `/api/v5/work-flow-activities/module-ref/${biaId}/module-name/BIA`
    );
  }

  getCallTrees(biaId) {
    return this.restApi
      .get(`/api/v5/bia-applicable-call-trees/${biaId}/bia-id`)
      .pipe(
        mergeMap((ctResponse) => {
          const { biaApplicableCallTreeDto, users } = ctResponse.body;
          const ctArray = biaApplicableCallTreeDto.map((ct) => {
            const staff = users.find((usr) => usr.id === ct.staffId);
            return { ...ct, staff };
          });

          return this.restApi
            .get('/api/v5/final-bia-applicable-call-trees', {
              page: 0,
              size: 2147483647,
              'biaId.equals': biaId,
            })
            .pipe(
              mergeMap((actResponse) => {
                const finalCallTreeArray = actResponse.body.filter((acts) =>
                  ctArray.some((ct) => acts.callTree.id === ct.id)
                );

                const mergedArray = ctArray.map((ct) => {
                  const finalCallTree = finalCallTreeArray.filter(
                    (acts) => acts.callTree.id === ct.id
                  );
                  return { ...ct, finalCallTree };
                });

                return of(mergedArray);
              })
            );
        })
      );
  }

  getCallTreeMultiple(biaIds) {
    return this.restApi
      .get('/api/v5/bia-applicable-call-trees', {
        'biaId.in': biaIds,
        page: 0,
        size: 1000,
      })
      .pipe(map((response) => response.body));
  }

  addAppCallTree(data) {
    return this.restApi.request(
      'POST',
      '/api/v5/final-bia-applicable-call-trees',
      data
    );
  }

  updateAppCallTree(data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/final-bia-applicable-call-trees/${data.id}`,
      data
    );
  }

  adjustAppCallTree(callTreeList) {
    let processes = callTreeList.toBeAddedAct.reduce((all, current, idx) => {
      all[`tbaa-${idx}`] = this.addAppCallTree(current);
      return all;
    }, {});

    processes = callTreeList.toBeUpdatedAct.reduce((all, current, idx) => {
      all[`tbua-${idx}`] = this.updateAppCallTree(current);
      return all;
    }, processes);

    return forkJoin(processes).pipe(map((response) => Object.values(response)));
  }

  saveAppCallTree(data) {
    return this.restApi.request(
      'POST',
      '/api/v5/final-bia-applicable-call-trees-all',
      data
    );
  }

  getActiveBias(options = {}) {
    return this.getBias({ ...options, 'isEnabled.equals': true });
  }

  getBias(options = {}) {
    return this.restApi.request('GET', '/api/v5/bias', options);
  }

  updateBiaAlongwithWorkFlow(biaId, data) {
    return this.restApi.request('PUT', `/api/v5/bias/${biaId}/workflow`, data);
  }

  // BIA - Peaktime Related

  getBiaPeakTime(biaId) {
    return this.restApi.get('/api/v5/bia-peak-times', {
      'biaId.equals': biaId,
      'isEnabled.equals': true,
    });
  }

  getActiveMdmPeaktimes(options = {}) {
    return this.restApi.request('GET', '/api/sa-peak-times', {
      ...options,
      'IsEnabled.equals': true,
      'IsActive.equals': true,
    });
  }

  addPeakTime(data) {
    return this.restApi.request('POST', '/api/v5/bia-peak-times', data);
  }

  // BIA - Critical Dealine Related
  getBiaDeadLine(biaId) {
    return this.restApi.get('/api/v5/bia-critical-deadlines', {
      'biaId.equals': biaId,
      'isEnabled.equals': true,
    });
  }

  addDeadLine(data) {
    return this.restApi.request('POST', '/api/v5/bia-critical-deadlines', data);
  }

  getActiveMdmCriticalDeadlines(options = {}) {
    return this.restApi.request('GET', '/api/sa-critical-deadlines', {
      ...options,
      'IsEnabled.equals': true,
      'IsActive.equals': true,
    });
  }

  // Internal Dependencies
  getInDependenciesForBias(biaIds: number[]) {
    const services = biaIds.reduce((all, biaId) => {
      all['in-service-' + biaId] = this.getInDependencies(biaId);
      return all;
    }, {});

    return forkJoin(services).pipe(
      map((resp: any[]) => {
        return Object.values(resp).reduce((all, deps) => {
          return [...all, ...deps];
        }, []);
      })
    );
  }

  getInDependencies(biaId) {
    return this.restApi
      .get(`/api/v5/bia-internal-dependencies/${biaId}/bia-id`)
      .pipe(
        map((res) =>
          res.body.map((inDep) => {
            const rto = inDep.overrideRtoDto || inDep.rtoDto;
            return {
              ...inDep.biaInternalDependencyDto,
              dependencyRto: rto ? BiaUtils.getTimeString(rto) : rto,
            };
          })
        )
      );
  }

  addInDependency(data) {
    return this.restApi.request(
      'POST',
      '/api/v5/bia-internal-dependencies',
      data
    );
  }

  updateInDependency(id, data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bia-internal-dependencies/${id}`,
      data
    );
  }

  deleteInDependency(id) {
    return this.restApi.request(
      'DELETE',
      `/api/v5/bia-internal-dependencies/${id}`
    );
  }

  // External Dependencies
  getExDependenciesForBias(biaIds: number[]) {
    return this.restApi
      .get('/api/v5/bia-external-dependencies', {
        'biaId.in': biaIds,
      })
      .pipe(map((resp) => resp.body));
  }
  getExDependencies(biaId) {
    return this.restApi.get('/api/v5/bia-external-dependencies', {
      'biaId.equals': biaId,
    });
  }

  getExDependency(id) {
    return this.restApi
      .get(`/api/v5/bia-external-dependencies/${id}`)
      .pipe(map((res: any) => res.body));
  }

  private prepareExternalDependency({ file, ...jsonData }) {
    const formData = new FormData();
    formData.append('biaExternalDependencyDto', JSON.stringify(jsonData));
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }
  addExDependency(data) {
    const formData = this.prepareExternalDependency(data);
    return this.restApi.request(
      'POST',
      '/api/v5/bia-external-dependencies/create',
      formData
    );
  }

  updateExDependency(id, data) {
    const formData = this.prepareExternalDependency(data);
    return this.restApi.request(
      'PUT',
      `/api/v5/bia-external-dependencies/${id}/update`,
      formData
    );
  }

  deleteExDependency(id) {
    return this.restApi.request(
      'DELETE',
      `/api/v5/bia-external-dependencies/${id}`
    );
  }

  // Process
  getProcessesForBias(biaIds, options = {}) {
    return this.restApi.get('/api/v5/bia-processes', {
      'biaId.in': biaIds,
      page: 0,
      size: 100000,
      ...options,
    });
  }

  getProcesses(biaId, options = {}) {
    return this.restApi.get('/api/v5/bia-processes', {
      page: 0,
      size: 100000,
      'biaId.equals': biaId,
      'isEnabled.equals': true,
      ...options,
    });
  }

  addProcess(data) {
    return this.restApi.request('POST', '/api/v5/bia-processes', data);
  }

  updateProcess(id, data) {
    return this.restApi.request('PUT', `/api/v5/bia-processes/${id}`, data);
  }

  softDeleteProcess(id) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bia-processes-soft-delete/${id}`
    );
  }

  getImpacts(biaId) {
    return this.restApi.get(`/api/v5/bia-impacts/${biaId}/bia-id`).pipe(
      map((impacts) => {
        return impacts.body.map(({ biaImpactDto, saRtoDto }) => {
          let rtoDto = {
            ...saRtoDto,
            totalsecond: BiaUtils.calculateSeconds(saRtoDto),
          };
          let overrideRtoValue = biaImpactDto.overrideRtoValue
            ? {
                ...biaImpactDto.overrideRtoValue,
                totalsecond: BiaUtils.calculateSeconds(
                  biaImpactDto.overrideRtoValue
                ),
              }
            : null;

          [rtoDto, overrideRtoValue] = BiaUtils.processTimeString([
            rtoDto,
            overrideRtoValue,
          ]);

          return { ...biaImpactDto, saRtoDto: rtoDto, overrideRtoValue };
        });
      })
    );
  }

  addImpact(data) {
    return this.restApi.request('POST', '/api/v5/bia-impacts', data);
  }

  updateImpact(id, data) {
    return this.restApi.request('PUT', `/api/v5/bia-impacts/${id}`, data);
  }

  // Additional Information
  getAdditionalInformation(biaId) {
    return this.restApi.get('/api/v5/bia-additional-infos', {
      'biaId.equals': biaId,
    });
  }

  private prepareAdditionalInformation({ file, ...jsonData }) {
    const formData = new FormData();
    formData.append('biaAdditionalInfoDto', JSON.stringify(jsonData));
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }

  addAdditionalInformation(data) {
    const formData = this.prepareAdditionalInformation(data);
    return this.restApi.request(
      'POST',
      '/api/v5/bia-additional-infos/create',
      formData
    );
  }

  updateAdditionalInformation(adInfoId, data) {
    const formData = this.prepareAdditionalInformation(data);
    return this.restApi.request(
      'PUT',
      `/api/v5/bia-additional-infos/${adInfoId}/update`,
      formData
    );
  }

  downloadFile(fileName) {
    return this.restApi.get(`/api/v5/downloadFile/${fileName}`, undefined, {
      responseType: 'blob',
    });
  }

  openFileAfterDownloading(response, file) {
    const blob = new Blob([response.body], { type: response.body.type });
    const downloadURL = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadURL;
    link.download = file.fileName;
    link.click();
  }

  // vital record
  getVitalRecord(biaId) {
    return this.restApi.get('/api/v5/bia-vital-records/' + biaId + '/bia-id');
  }

  addVitalRecord(data) {
    return this.restApi.request('POST', '/api/v5/bia-vital-records', data);
  }
  updateVitalRecord(vitalId, data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bia-vital-records/${vitalId}`,
      data
    );
  }
  // it infra
  getItInfra(biaId) {
    return this.restApi.get('/api/v5/bia-it-infrastructures', {
      'biaId.equals': biaId,
    });
  }
  addItInfra(data) {
    return this.restApi.request('POST', '/api/v5/bia-it-infrastructures', data);
  }
  updateItInfra(infraId, data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bia-it-infrastructures/${infraId}`,
      data
    );
  }
  // special req
  getSplReq(biaId) {
    return this.restApi.get('/api/v5/bia-special-requirements', {
      'biaId.equals': biaId,
    });
  }
  addSplReq(data) {
    return this.restApi.request(
      'POST',
      '/api/v5/bia-special-requirements',
      data
    );
  }
  updateSplReq(sqlId, data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bia-special-requirements/${sqlId}`,
      data
    );
  }
  // application and software
  getAppNSoft(biaId) {
    return this.restApi.get('/api/v5/bia-application-softwares', {
      'biaId.equals': biaId,
    });
  }
  addAppNSoft(data) {
    return this.restApi.request(
      'POST',
      '/api/v5/bia-application-softwares',
      data
    );
  }
  updateAppsNSoft(appsNsoftId, data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bia-application-softwares/${appsNsoftId}`,
      data
    );
  }
  deleteAppNSoft(id) {
    return this.restApi.request(
      'DELETE',
      `/api/v5/bia-application-softwares/${id}`
    );
  }
  // staff requirement
  getStaffReq(biaId) {
    return this.restApi.get('/api/v5/bia-staff-requirements', {
      'biaId.equals': biaId,
    });
  }
  addStaffReq(data) {
    return this.restApi.request('POST', '/api/v5/bia-staff-requirements', data);
  }
  updateStaffReq(staffReqId, data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bia-staff-requirements/${staffReqId}`,
      data
    );
  }
  deleteStaffReq(staffReqId) {
    return this.restApi.request(
      'DELETE',
      `/api/v5/bia-staff-requirements/${staffReqId}`
    );
  }
  // SPOF
  getSpof(biaId) {
    return this.restApi.get('/api/v5/bia-single-point-failures', {
      'biaId.equals': biaId,
    });
  }
  updateSpof(id, data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bia-single-point-failures/${id}`,
      data
    );
  }
  addSpof(data) {
    return this.restApi.request(
      'POST',
      '/api/v5/bia-single-point-failures',
      data
    );
  }

  getOverrideRto(data = {}) {
    return this.restApi.get('/api/v5/sa-override-rtos', {
      data,
      'isEnabled.equals': true,
      'isActive.equals': true,
      page: 0,
      size: 10000,
    });
  }

  getEquipmentDevicesSharedList(biaId) {
    return this.restApi
      .get('/api/v5/bia-summary-equipment-devices', {
        'biaId.equals': biaId,
      })
      .pipe(map((response) => response.body));
  }

  addSaBiaIntro(data) {
    return this.restApi.request('POST', '/api/v5/sa-bia-intros', data);
  }

  updateSaBiaIntro(id, data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/sa-bia-intros/${id}/update`,
      data
    );
  }

  deleteSaBiaIntroFile(id: any, fileId: number): Observable<any> {
    return this.restApi.request(
      'DELETE',
      `/api/v5/sa-bia-intros/${id}/file/${fileId}`
    );
  }

  getSaBiaInto() {
    return this.restApi.get('/api/v5/sa-bia-intros', {});
  }

  private prepareBiaIntros({ file, ...jsonData }) {
    const formData = new FormData();
    formData.append('biaIntroDto', JSON.stringify(jsonData));
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }

  addBiaIntro(data) {
    const formData = this.prepareBiaIntros(data);
    return this.restApi.request('POST', '/api/v5/bia-intros/create', formData);
  }

  updateBiaIntro(id, data) {
    return this.restApi.request('PUT', `/api/v5/bia-intros/${id}/update`, data);
  }

  deleteBiaIntroFile(id: any, fileId: number): Observable<any> {
    return this.restApi.request(
      'DELETE',
      `/api/v5/bia-intros/${id}/file/${fileId}`
    );
  }

  getSaHardware() {
    return this.restApi
      .get('/api/v5/sa-hardwares', { page: 0, size: 100000 })
      .pipe(map((response) => response.body));
  }

  // HR Staff
  getHR(biaId) {
    return this.restApi.get('/api/v5/bia-hrs', {
      'biaId.equals': biaId,
    });
  }

  addHR(data) {
    return this.restApi.request('POST', '/api/v5/bia-hrs', data);
  }
  updateHR(id, data) {
    return this.restApi.request('PUT', `/api/v5/bia-hrs/${id}`, data);
  }
  deleteHR(id) {
    return this.restApi.request('DELETE', `/api/bia-hrs/${id}`);
  }

  // Resource Call Tree
  getResourceCallTree(biaId) {
    return this.restApi.get('/api/v5/bia-resource-call-trees', {
      'biaId.equals': biaId,
    });
  }
  addResourceCallTree(data) {
    return this.restApi.request(
      'POST',
      '/api/v5/bia-resource-call-trees',
      data
    );
  }
  updateResourceCallTree(id, data) {
    return this.restApi.request(
      'PUT',
      `/api/v5/bia-resource-call-trees/${id}`,
      data
    );
  }
  deleteResourceCallTree(id) {
    return this.restApi.request(
      'DELETE',
      `/api/v5/bia-resource-call-trees/${id}`
    );
  }

  // Bia Intro starts
  getBiaIntro(biaId) {
    return this.restApi.get('/api/v5/bia-intros', {
      'biaId.equals': biaId,
    });
  }

  downloadModuleStatus(moduleName) {
    const httpOptions: any = {
      headers: new HttpHeaders({
        Accept: 'application/octet-stream',
        'Cache-Control':
          'no-cache, no-store, must-revalidate, post- check=0, pre-check=0',
        Pragma: 'no-cache',
        Expires: '0',
      }),
      responseType: 'blob',
    };
    return this.http.get(
      `${environment.apiUrl}/api/v5/${moduleName}/status`,
      httpOptions
    );
  }
}
